import React, { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router';
import pluralize from 'pluralize';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import useAccountDownloadsConfigurator from './hooks/useAccountDownloadsConfigurator';
import FullScreenLoader from '../../../../../components/global/FullScreenLoader';
import DesignEditorPreviewPane from '../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import { getBackgroundAccountCardElement } from '../../../../../services/cards/views/AccountCardConfigurator/helpers';
import DesignEditor from '../../../../../components/global/DesignEditor';
import Button from '../../../../../components/global/Button';
import DesignEditorHeader, {
  DesignEditorHeaderCenterSlot,
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import DownloadsConfiguratorPreviewPane from './components/DownloadsConfiguratorPreviewPane';
import { getCardPhotoElementsWithConfigurationFromAccountCard } from '../../../../../services/prints/components/PrintProductGroupConfigurator/helpers';
import CardElementPhotoSelectionControl from '../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import uuidv4 from '../../../../../util/uuid';
import DownloadsConfiguratorSuccessModal from './DownloadsConfiguratorSuccessModal';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../util/normalize-errors';
import Angle from '../../../../../components/global/Angle';
import useAccount from '../../../../../components/global/hooks/useAccount';
import DownloadsConfiguratorEmailModal from './components/DownloadsConfiguratorEmailModal';

const AccountDownloadsConfigurator = () => {
  const history = useHistory();
  const openAlert = useOpenAlert();
  const location = useLocation();
  const [lastUsedEmailAddress, setLastUsedEmailAddress] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [lastUsedPrintBackground, setLastUsedPrintBackground] = useState();
  const [printBackgroundModalOpen, setPrintBackgroundModalOpen] = useState(
    false,
  );
  const [photoSelectorOpen, setPhotoSelectorOpen] = useState(false);

  const { account } = useAccount();
  const { source } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });

  const isIdentitiesPath = source === 'identities';

  const {
    identityId,
    albumFolder,
    hierarchyItem,
    loadingAlbumFolder,
    accountCardConfigurations,
    setAccountCardConfigurations,
    activeAccountCardConfiguration,
    setActiveAccountCardConfiguration,
    activePhoto,
    setActivePhoto,
    printAssetBackgrounds,
    loadingPhotos,
    fetchedPhotos,
    setFetchedPhotos,
    fetchedPhotosTotal,
    submitting,
    setSubmitting,
    onAccountCardsPhotoDownloadCreate,
  } = useAccountDownloadsConfigurator();

  useEffect(() => {
    if (!loadingPhotos && fetchedPhotosTotal === 0 && !activePhoto) {
      setPhotoSelectorOpen(true);
    }
  }, [loadingPhotos, fetchedPhotos]);

  useEffect(() => {
    if (printAssetBackgrounds?.length > 0 && !lastUsedPrintBackground) {
      setLastUsedPrintBackground(printAssetBackgrounds[0]);
    }
  }, [printAssetBackgrounds?.length]);

  useEffect(() => {
    if (account?.email_address) {
      setLastUsedEmailAddress(account.email_address);
    }
  }, [account]);

  const {
    accountCard: activeAccountCard,
    accountCardElementReferences: activeAccountCardElementReferences,
  } = activeAccountCardConfiguration ?? {};

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard: activeAccountCard,
  });

  const photoElements = getCardPhotoElementsWithConfigurationFromAccountCard({
    accountCard: activeAccountCard,
  }).filter(
    elem =>
      elem?.cardElement?.id !== backgroundElement?.cardElement?.id &&
      (!elem.cardElementConfiguration ||
        elem.cardElementConfiguration?.is_editable),
  );

  const [mainPhotoElement] = photoElements ?? [];

  const photoElementReference =
    activeAccountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  const classes = classNames({
    'print-product-configurator': true,
    'downloads-configurator': true,
  });

  const handleNextItem = async (emailAddress = lastUsedEmailAddress) => {
    if (submitting) {
      return;
    }

    if (
      !fetchedPhotosTotal ||
      fetchedPhotosTotal === accountCardConfigurations?.length
    ) {
      if (!emailAddress) {
        setEmailModalOpen(true);
        return;
      }

      try {
        setSubmitting(true);
        await onAccountCardsPhotoDownloadCreate(emailAddress);
        setLastUsedEmailAddress(emailAddress);
        setSuccessModalOpen(true);
      } catch (e) {
        openAlert({
          message: 'There was an error generating your download.',
          info: getErrorString(e),
        });
      } finally {
        setSubmitting(false);
      }
    } else {
      const currentIndex = fetchedPhotos.findIndex(
        p => p.id === activePhoto?.id,
      );
      setActivePhoto(fetchedPhotos[currentIndex + 1]);
      setActiveAccountCardConfiguration(undefined);
    }
  };

  if (loadingAlbumFolder || loadingPhotos || !albumFolder) {
    return <FullScreenLoader />;
  }

  return (
    <DesignEditor className={classes}>
      <DesignEditorHeader>
        <DesignEditorHeaderLeftSlot>
          <Button
            btnStyle="link"
            onClick={() => {
              if (isIdentitiesPath) {
                history.push(
                  `/app/shutterbug/identities${
                    identityId ? `/${identityId}` : ''
                  }`,
                );
              } else {
                history.push(
                  `/app/shutterbug/hierarchy-items/${hierarchyItem?.id}`,
                );
              }
            }}
          >
            <Angle direction="left" className="d-none d-md-inline-block" /> Back
          </Button>
        </DesignEditorHeaderLeftSlot>
        <DesignEditorHeaderCenterSlot>
          <h3 className="design-editor-header-title">
            Configure your {pluralize('download', fetchedPhotosTotal ?? 0)}{' '}
            {fetchedPhotosTotal > 1
              ? ` (${accountCardConfigurations?.length}/${fetchedPhotosTotal})`
              : ''}
          </h3>
        </DesignEditorHeaderCenterSlot>
        <DesignEditorHeaderRightSlot />
      </DesignEditorHeader>

      <DesignEditorPreviewPane className="downloads-configurator-preview-pane">
        <div className="downloads-configurator-actions-container">
          {photoElementReference && photoElements?.length === 1 && (
            <Fragment>
              {photoElementReference?.onAdjustAndCrop && (
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="text-extra-bold"
                  onClick={() => {
                    photoElementReference.onAdjustAndCrop();
                  }}
                >
                  Adjust &amp; Crop
                </Button>
              )}
            </Fragment>
          )}
          {printAssetBackgrounds?.length > 0 && !!backgroundElement && (
            <Button
              btnStyle="link"
              btnSize="sm"
              className="text-extra-bold"
              onClick={() => setPrintBackgroundModalOpen(true)}
            >
              {backgroundElement?.cardElementConfiguration?.asset_image?.id
                ? 'Change Backdrop'
                : 'Add Backdrop'}
            </Button>
          )}
        </div>
        <DownloadsConfiguratorPreviewPane
          key={activePhoto?.uuid}
          defaultPhoto={activePhoto}
          defaultPrintBackground={lastUsedPrintBackground}
          printBackgrounds={printAssetBackgrounds}
          printBackgroundModalOpen={printBackgroundModalOpen}
          setPrintBackgroundModalOpen={setPrintBackgroundModalOpen}
          setLastUsedPrintBackground={setLastUsedPrintBackground}
          onAccountCardUpdated={({
            accountCard,
            accountCardElementReferences,
          }) => {
            if (!accountCard) {
              return;
            }

            const updatedConfig = {
              photo: activePhoto,
              accountCard,
              accountCardElementReferences,
            };

            if (activeAccountCardConfiguration) {
              setAccountCardConfigurations(current => [
                ...current.filter(
                  config => config.accountCard?.id !== accountCard?.id,
                ),
                updatedConfig,
              ]);
            } else {
              setAccountCardConfigurations(current => [
                ...current,
                updatedConfig,
              ]);
            }
            setActiveAccountCardConfiguration(updatedConfig);
          }}
        />
        {activeAccountCard && (
          <div className="downloads-configurator-continue-wrap">
            <Button
              btnBlock
              btnSize="lg"
              onClick={() => handleNextItem()}
              disabled={submitting}
              btnStyle="primary"
              data-testid="downloads-configurator-continue"
              className="downloads-configurator-continue"
            >
              {(() => {
                if (submitting) {
                  return <Fragment>Finalizing configurations...</Fragment>;
                }

                return (
                  <Fragment>
                    I&apos;m happy with this!
                    <div className="text-xs text-normal">
                      {!fetchedPhotosTotal ||
                      fetchedPhotosTotal === accountCardConfigurations?.length
                        ? 'Finalize all configurations.'
                        : `Configure next photo.`}
                    </div>
                  </Fragment>
                );
              })()}
            </Button>
          </div>
        )}
      </DesignEditorPreviewPane>
      <CardElementPhotoSelectionControl
        title="Pick the photos you'd like to configure"
        forceOpen={photoSelectorOpen}
        allowDismiss={!!activePhoto}
        onClose={() => setPhotoSelectorOpen(false)}
        maxPhotosCount={999}
        maxPhotosCountStrict={false}
        onChange={photos => {
          // Add UUID so we can properly work with multiple of the same photo
          // in the event that the user tries configuring this in multiple instances.
          const parsedPhotos = photos.map(photo => ({
            ...photo,
            uuid: uuidv4(),
          }));
          setFetchedPhotos(parsedPhotos);
          setActivePhoto(parsedPhotos?.[0]);
          setActiveAccountCardConfiguration(undefined);
        }}
        photosContext={{
          album_id: hierarchyItem?.album?.id ?? '',
          album_folder_id: hierarchyItem?.album_folder?.id ?? '',
          root_album_folder_id: albumFolder?.id,
          identity_id: identityId,
        }}
        mappings={[
          {
            id: 'entitled',
            label: 'Photos',
            path: 'entitled_photos',
          },
        ]}
        uploadDisabled
      />
      <DownloadsConfiguratorSuccessModal
        forceOpen={successModalOpen}
        onConfigureMore={() => {
          setFetchedPhotos([]);
          setActivePhoto(undefined);
          setAccountCardConfigurations([]);
          setActiveAccountCardConfiguration(undefined);
          setSuccessModalOpen(false);
        }}
        onBack={() => {
          if (isIdentitiesPath) {
            history.push(
              `/app/shutterbug/identities${identityId ? `/${identityId}` : ''}`,
            );
          } else {
            history.push(
              `/app/shutterbug/hierarchy-items/${hierarchyItem?.id}`,
            );
          }
          setSuccessModalOpen(false);
        }}
        downloadsCount={fetchedPhotosTotal}
        buttonLabel={isIdentitiesPath ? 'Back to My People' : 'Back to Event'}
      />
      <DownloadsConfiguratorEmailModal
        forceOpen={emailModalOpen}
        onSubmit={emailAddress => handleNextItem(emailAddress)}
        onClose={() => setEmailModalOpen(false)}
        downloadsCount={fetchedPhotosTotal}
      />
    </DesignEditor>
  );
};

export default AccountDownloadsConfigurator;
